<template>
  <b-modal
    v-model="showModal"
    size="xl"
    id="stock-changes-modal"
    title="Manage Stock"
    :ok-title="isSaveLoading ? 'Saving...' : 'Save'"
    :ok-disabled="isSaveLoading || isContentLoading"
    cancel-variant="outline-secondary"
    @ok="onSubmit($event)"
  >
    <div
      v-if="isContentLoading"
      class="text-center py-2"
    >
      <b-spinner label="Loading..." />
    </div>

    <div v-else>
      <div
        v-if="unit"
        class="d-flex align-items-center mb-1"
      >
        <div class="unit-stock-changes-thumb">
          <img
            :src="unitPictureSrc"
            alt=""
            class="unit-stock-changes-img"
          >
        </div>
        <span class="ml-2">{{ unit.name }}</span>
      </div>

      <div
        v-if="editedStocks.length === 0"
        class="text-muted"
      >
        No stock locations found for this company.
      </div>

      <div
        v-else
        class="stock-table-head mb-1 p-1 border rounded"
      >
        <b-row class="align-items-center font-weight-bold">
          <b-col cols="12" sm="6">Location</b-col>
          <b-col cols="12" sm="3" class="mt-1 mt-sm-0">Receive</b-col>
          <b-col cols="12" sm="3" class="mt-1 mt-sm-0">Take Out</b-col>
        </b-row>
      </div>

      <div
        v-for="stock in editedStocks"
        :key="stock.stock_location_id"
        class="mb-1 border rounded p-1"
      >
        <b-row class="align-items-center">
          <b-col cols="12" sm="6" class="d-flex align-items-center">
            <div
              class="stock-location-thumb"
              :class="{ 'stock-location-thumb--empty': !stock.picture }"
            >
              <img
                v-if="stock.picture"
                :src="pictureUrl(stock.picture)"
                alt=""
                class="stock-location-img"
              >
            </div>
            <div class="ml-1">
              <div><strong>{{ stock.name }}</strong></div>
              <small class="text-muted">Current: {{ stock.quantity }}</small>
            </div>
          </b-col>
          <b-col cols="12" sm="3" class="mt-1 mt-sm-0">
            <b-form-input
              :value="stock.in_quantity"
              type="number"
              min="0"
              step="1"
              placeholder="Receive"
              inputmode="numeric"
              @keydown="onQuantityKeydown"
              @input="setQuantity(stock, 'in_quantity', $event)"
            />
            <div
              v-show="parseQuantity(stock.in_quantity) > 0"
              class="stock-supplier-search mt-1"
            >
              <b-form-input
                :value="stock.supplier_search"
                size="sm"
                placeholder="Supplier (optional)"
                autocomplete="off"
                @input="onSupplierSearchInput(stock, $event)"
              />
              <div
                v-if="stock.selected_supplier"
                class="stock-supplier-selected d-flex align-items-center mt-1"
              >
                <div
                  class="stock-supplier-result-thumb"
                  :class="{ 'stock-supplier-result-thumb--empty': !stock.selected_supplier.image }"
                >
                  <img
                    v-if="stock.selected_supplier.image"
                    :src="pictureUrl(stock.selected_supplier.image)"
                    alt=""
                    class="stock-supplier-result-img"
                  >
                </div>
                <span class="ml-1 flex-grow-1 text-truncate">{{ stock.selected_supplier.name }}</span>
                <b-button
                  size="sm"
                  variant="link"
                  class="p-0"
                  @click="clearSupplier(stock)"
                >
                  Clear
                </b-button>
              </div>
              <div
                v-else-if="stock.supplier_search_loading"
                class="text-muted small mt-1"
              >
                Searching...
              </div>
              <ul
                v-else-if="stock.supplier_results.length > 0"
                class="stock-supplier-results list-unstyled mb-0 mt-1"
              >
                <li
                  v-for="supplier in stock.supplier_results"
                  :key="supplier.id"
                  class="stock-supplier-result-item d-flex align-items-center"
                  @click="selectSupplier(stock, supplier)"
                >
                  <div
                    class="stock-supplier-result-thumb"
                    :class="{ 'stock-supplier-result-thumb--empty': !supplier.image }"
                  >
                    <img
                      v-if="supplier.image"
                      :src="pictureUrl(supplier.image)"
                      alt=""
                      class="stock-supplier-result-img"
                    >
                  </div>
                  <span class="ml-1 text-truncate">{{ supplier.name }}</span>
                </li>
              </ul>
            </div>
          </b-col>
          <b-col cols="12" sm="3" class="mt-1 mt-sm-0">
            <b-form-input
              :value="stock.out_quantity"
              type="number"
              min="0"
              :max="Math.max(0, stock.quantity)"
              step="1"
              placeholder="Take out"
              inputmode="numeric"
              :disabled="stock.quantity <= 0"
              @keydown="onQuantityKeydown"
              @input="setQuantity(stock, 'out_quantity', $event)"
            />
            <b-form-textarea
              v-show="parseQuantity(stock.out_quantity) > 0"
              v-model="stock.notes"
              size="sm"
              rows="2"
              max-rows="4"
              placeholder="Notes (optional)"
              class="mt-1 stock-out-notes"
            />
          </b-col>
        </b-row>
      </div>
    </div>
  </b-modal>
</template>

<script>
import {
  BButton,
  BCol,
  BFormInput,
  BFormTextarea,
  BModal,
  BRow,
  BSpinner,
} from 'bootstrap-vue'
import { mapActions } from 'vuex'
import { toastFailure, toastSuccess } from '@/libs/toastification'

export default {
  components: {
    BModal,
    BRow,
    BCol,
    BButton,
    BFormInput,
    BFormTextarea,
    BSpinner,
  },
  props: {
    unit: {
      type: Object,
      default: null,
    },
    clickDate: {
      type: Date,
      required: true,
    },
  },
  data() {
    return {
      showModal: false,
      isContentLoading: true,
      isSaveLoading: false,
      editedStocks: [],
      supplierSearchTimers: {},
    }
  },
  computed: {
    unitPictureSrc() {
      const pics = this.unit && this.unit.picture
      if (Array.isArray(pics) && pics[0] && pics[0].src) {
        return pics[0].src
      }
      return ''
    },
  },
  watch: {
    clickDate() {
      if (!this.unit || !this.unit.id) {
        return
      }

      this.isContentLoading = true
      this.showModal = true
      this.loadUnitStockLocations(this.unit.id)
        .then((rows) => {
          this.editedStocks = rows.map((row) => this.mapStockRow(row))
        })
        .catch(() => {
          this.showModal = false
          toastFailure('Failed to load stock locations.')
        })
        .finally(() => {
          this.isContentLoading = false
        })
    },
  },
  methods: {
    ...mapActions({
      loadUnitStockLocations: 'stockChanges/loadUnitStockLocations',
      storeStockChanges: 'stockChanges/storeStockChanges',
      searchSuppliers: 'suppliers/searchSuppliers',
    }),
    mapStockRow(row) {
      return {
        stock_location_id: row.id,
        name: row.name,
        picture: row.picture || null,
        quantity: Number(row.quantity || 0),
        in_quantity: '',
        out_quantity: '',
        supplier_search: '',
        supplier_results: [],
        supplier_search_loading: false,
        selected_supplier: null,
        notes: '',
      }
    },
    inputValue(raw) {
      if (raw && typeof raw === 'object' && raw.target != null) {
        return String(raw.target.value ?? '')
      }
      if (raw !== null && raw !== undefined) {
        return String(raw)
      }
      return ''
    },
    onSupplierSearchInput(stock, raw) {
      const value = this.inputValue(raw)
      const keyword = value.trim()
      stock.supplier_search = value

      if (
        stock.selected_supplier
        && keyword !== String(stock.selected_supplier.name || '').trim()
      ) {
        stock.selected_supplier = null
      }

      if (this.supplierSearchTimers[stock.stock_location_id]) {
        clearTimeout(this.supplierSearchTimers[stock.stock_location_id])
      }

      if (keyword.length < 2) {
        stock.supplier_results = []
        stock.supplier_search_loading = false
        return
      }

      stock.supplier_search_loading = true
      this.supplierSearchTimers[stock.stock_location_id] = setTimeout(() => {
        this.searchSuppliers(keyword)
          .then((results) => {
            stock.supplier_results = results || []
          })
          .catch(() => {
            stock.supplier_results = []
          })
          .finally(() => {
            stock.supplier_search_loading = false
          })
      }, 300)
    },
    selectSupplier(stock, supplier) {
      stock.selected_supplier = { ...supplier }
      stock.supplier_search = supplier.name
      stock.supplier_results = []
    },
    clearSupplier(stock) {
      stock.selected_supplier = null
      stock.supplier_search = ''
      stock.supplier_results = []
    },
    pictureUrl(picture) {
      if (!picture) {
        return ''
      }
      const p = String(picture).trim()
      if (!p) {
        return ''
      }
      if (/^https?:\/\//i.test(p) || p.startsWith('//')) {
        return p
      }
      return p.startsWith('/') ? p : `/${p}`
    },
    parseQuantity(value) {
      if (value === '' || value === null || value === undefined) {
        return 0
      }
      const n = parseInt(String(value), 10)
      return Number.isNaN(n) || n < 0 ? 0 : n
    },
    setQuantity(stock, field, raw) {
      let str = ''
      if (raw && typeof raw === 'object' && raw.target != null) {
        str = String(raw.target.value)
      } else if (raw !== null && raw !== undefined && raw !== '') {
        str = String(raw)
      }

      if (str === '') {
        stock[field] = ''
        return
      }

      let parsed = parseInt(str, 10)
      if (Number.isNaN(parsed) || parsed < 0) {
        stock[field] = ''
        return
      }

      if (field === 'out_quantity') {
        if (stock.quantity <= 0 || parsed <= 0) {
          stock[field] = ''
          return
        }
        if (parsed > stock.quantity) {
          parsed = stock.quantity
        }
      }

      stock[field] = String(parsed)
    },
    onQuantityKeydown(e) {
      if (['e', 'E', '+', '-', '.', ','].includes(e.key)) {
        e.preventDefault()
      }
    },
    onSubmit(event) {
      event.preventDefault()

      if (this.isSaveLoading || !this.unit || !this.unit.id) {
        return
      }

      const stocks = this.editedStocks.map((row) => {
        const payload = {
          stock_location_id: row.stock_location_id,
          in_quantity: this.parseQuantity(row.in_quantity),
          out_quantity: this.parseQuantity(row.out_quantity),
        }

        if (row.selected_supplier?.id) {
          payload.supplier_id = row.selected_supplier.id
        }

        const notes = String(row.notes || '').trim()
        if (notes !== '') {
          payload.notes = notes
        }

        return payload
      })

      const hasChange = stocks.some((row) => row.in_quantity > 0 || row.out_quantity > 0)
      if (!hasChange) {
        toastFailure('Please enter receive or out quantity for at least one location.')
        return
      }

      const invalidRow = stocks.find((row, index) => (
        row.out_quantity > 0
        && row.out_quantity > this.editedStocks[index].quantity
      ))
      if (invalidRow) {
        toastFailure('Out quantity can not exceed current quantity.')
        return
      }

      this.isSaveLoading = true
      this.storeStockChanges({
        unitId: this.unit.id,
        stocks,
      })
        .then((response) => {
          const updatedRows = response.data || []
          this.editedStocks = updatedRows.map((row) => this.mapStockRow(row))
          const totalStock = updatedRows.reduce(
            (sum, row) => sum + Number(row.quantity || 0),
            0,
          )
          this.$emit('stock-updated', {
            unitId: this.unit.id,
            totalStock,
          })
          this.showModal = false
          toastSuccess('Stock updated successfully.')
        })
        .catch((error) => {
          const message = error?.response?.data?.message || 'Failed to update stock.'
          toastFailure(message)
        })
        .finally(() => {
          this.isSaveLoading = false
        })
    },
  },
}
</script>

<style scoped>
.stock-table-head {
  background: #f8f8f8;
}

.stock-location-thumb {
  width: 150px;
  height: 150px;
  border: 1px solid #ddd;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #fafafa;
  box-sizing: border-box;
}

.stock-location-thumb--empty {
  background: #f5f5f5;
}

.stock-location-img {
  max-width: 150px;
  max-height: 150px;
  width: auto;
  height: auto;
  object-fit: contain;
  vertical-align: middle;
}

.unit-stock-changes-thumb {
  width: 56px;
  height: 56px;
  flex-shrink: 0;
  border: 2px solid #9e9e9e;
  box-sizing: border-box;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.unit-stock-changes-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  vertical-align: middle;
}

.stock-out-notes {
  resize: vertical;
}

.stock-supplier-results {
  max-height: 160px;
  overflow-y: auto;
  border: 1px solid #ddd;
  border-radius: 0.25rem;
  background: #fff;
}

.stock-supplier-result-item {
  padding: 0.35rem 0.5rem;
  cursor: pointer;
}

.stock-supplier-result-item:hover {
  background: #f5f5f5;
}

.stock-supplier-result-thumb {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border: 1px solid #ddd;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #fafafa;
}

.stock-supplier-result-thumb--empty {
  background: #f0f0f0;
}

.stock-supplier-result-img {
  max-width: 32px;
  max-height: 32px;
  object-fit: contain;
}
</style>
